import React, { useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Spinner } from '@iq/react-components';
import { useDispatch } from 'react-redux';

import { ComponentPanelProvider, useComponentPanel } from './ComponentPanelState';
import ComponentModal from '../../SiteAdminView/ComponentView/components/ComponentModal';
import Empty from '../../Empty';
import ConfirmationDialog from '../../ConfirmationDialog';
import ComponentTree from './components/ComponentTree';
import { createComponent } from '../../../bundles/components';

const ComponentsPanelContent = () => {
  const dispatch = useDispatch();
  const {
    // State from context
    pageComponents,
    components,
    componentsLoaded,
    tree,
    site,
    updating,
    creating,
    deleting,
    showDeleteDialog,
    canEdit,
    canDelete,
    modelsHashmap,

    // Action handlers
    setDeleting,
    setShowDeleteDialog,
    handleUpdateComponent,
    handleCloseUpdating,
    handleCreateComponent,
    handleCloseCreating,
    handleDeleteNonSiteComponents,

    // Effects
    requestInitialData,
    updatePageComponents,
    updatePollingVars,
    updateHasRootModel,
    setupDeletingComponent,
    executeDeleteComponent,
  } = useComponentPanel();

  useEffect(() => {
    requestInitialData();
  }, [requestInitialData]);

  useEffect(() => {
    updatePageComponents();
  }, [updatePageComponents]);

  useEffect(() => {
    updatePollingVars();
  }, [updatePollingVars]);

  useEffect(() => {
    updateHasRootModel();
  }, [updateHasRootModel]);

  useEffect(() => {
    setupDeletingComponent();
  }, [setupDeletingComponent]);

  useEffect(() => {
    executeDeleteComponent();
  }, [executeDeleteComponent]);

  const needsSite = componentsLoaded && !tree?.children;
  const empty = componentsLoaded && !needsSite && pageComponents.length === 0;

  if (needsSite && canEdit) {
    return (
      <section className="components-panel centered-content">
        <Button
          activity="primary"
          type="text"
          onClick={() =>
            dispatch(
              createComponent(site.id, site.org, {
                component: {
                  name: site.name,
                  type: 'site',
                  parent: null,
                  itemDesignation: '-',
                },
              })
            )
          }
        >
          Add Component
        </Button>
      </section>
    );
  }

  if (empty) {
    return <Empty text="No available components" />;
  }

  if (pageComponents.length === 0) {
    return (
      <div className="loading-container">
        <Spinner
          size="m"
          className="spinner"
        />
      </div>
    );
  }

  return (
    <section className={`components-panel ${empty ? 'centered-content' : ''}`}>
      <ComponentTree />
      {canEdit && (updating.show || creating.show) && (
        <ComponentModal
          modelsHashmap={modelsHashmap}
          onSubmit={updating.show ? handleUpdateComponent : handleCreateComponent}
          onCloseModal={updating.show ? handleCloseUpdating : handleCloseCreating}
          saveButtonText={updating.show ? 'Update' : 'Create'}
          updateId={updating.componentId}
          parentId={creating.parent}
        />
      )}
      {canDelete && deleting.show && (
        <ConfirmationDialog
          onCancel={() => setDeleting({ show: false, confirmed: false })}
          onConfirm={() =>
            setDeleting((previous) => ({ ...previous, show: false, confirmed: true }))
          }
          title="Remove Component"
          subtitle={`${deleting.component?.name}, ${deleting.component?.itemDesignation}`}
          body={
            <>
              <p>
                {'This will also remove all child components, variables, event links and references ' +
                  'associated with the component.'}
              </p>
              <p style={{ paddingTop: '1.5rem' }}>
                This is a destructive action and cannot be un-done.
              </p>
            </>
          }
          confirmText="Remove"
          confirmType="danger"
        />
      )}
      {showDeleteDialog && (
        <ConfirmationDialog
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={() => {
            handleDeleteNonSiteComponents();
            setShowDeleteDialog(false);
          }}
          title="Remove component tree"
          body={
            <>
              <p>
                {'This will remove all nonsite components, variables, event links and references ' +
                  'associated with the component.'}
              </p>
              <p style={{ paddingTop: '1.5rem' }}>
                This is a destructive action and cannot be un-done.
              </p>
            </>
          }
          confirmText="Remove"
          confirmType="danger"
        />
      )}
    </section>
  );
};

const ComponentsPanel = (properties) => (
  <ComponentPanelProvider {...properties}>
    <ComponentsPanelContent />
  </ComponentPanelProvider>
);

export default withRouter(ComponentsPanel);
